import React, { useState, useEffect } from 'react';
import { userManagementAPI } from '../services/api';
import './UserManagementPage.css';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  department?: string;
  phoneNumber?: string;
  roles: string[];
  createdDate: string;
  isActive: boolean;
}

interface AvailableRole {
  roleId: string;
  roleName: string;
  qrCode?: string;
  location?: string;
  isCustom: boolean;
}

interface CreateUserData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  department: string;
  phoneNumber: string;
  roles: string[];
}

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [createUserData, setCreateUserData] = useState<CreateUserData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    department: '',
    phoneNumber: '',
    roles: []
  });

  // Load users and available roles
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse] = await Promise.all([
        userManagementAPI.getAllUsers(),
        userManagementAPI.getAvailableRoles()
      ]);
      
      setUsers(usersResponse.data || []);
      setAvailableRoles(rolesResponse.data || []);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      // Hata durumunda boş arrayler set et, alert gösterme
      setUsers([]);
      setAvailableRoles([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle create user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createUserData.email || !createUserData.password || !createUserData.firstName || !createUserData.lastName) {
      alert('Lütfen zorunlu alanları doldurun!');
      return;
    }

    try {
      await userManagementAPI.createUser({
        ...createUserData,
        roles: selectedRoles
      });
      
      alert('Kullanıcı başarıyla oluşturuldu!');
      setShowCreateForm(false);
      resetForm();
      loadData();
    } catch (error: any) {
      console.error('Kullanıcı oluşturma hatası:', error);
      alert(error.response?.data?.message || 'Kullanıcı oluşturulamadı!');
    }
  };

  // Handle update user roles
  const handleUpdateUserRoles = async () => {
    if (!editingUser) return;

    try {
      await userManagementAPI.updateUserRoles(editingUser.id, { roles: selectedRoles });
      alert('Kullanıcı rolleri başarıyla güncellendi!');
      setEditingUser(null);
      setSelectedRoles([]);
      loadData();
    } catch (error: any) {
      console.error('Rol güncelleme hatası:', error);
      alert(error.response?.data?.message || 'Roller güncellenemedi!');
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await userManagementAPI.deleteUser(userId);
      alert('Kullanıcı başarıyla silindi!');
      loadData();
    } catch (error: any) {
      console.error('Kullanıcı silme hatası:', error);
      alert(error.response?.data?.message || 'Kullanıcı silinemedi!');
    }
  };

  // Reset form
  const resetForm = () => {
    setCreateUserData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      department: '',
      phoneNumber: '',
      roles: []
    });
    setSelectedRoles([]);
  };

  // Handle role selection
  const handleRoleToggle = (roleId: string) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  // Start editing user roles
  const startEditUserRoles = (user: User) => {
    setEditingUser(user);
    setSelectedRoles(user.roles);
  };

  if (loading) {
    return (
      <div className="user-management-container">
        <div className="loading">Kullanıcılar yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      <div className="user-management-header">
        <h1>Kullanıcı Yönetimi</h1>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="create-user-button"
        >
          ➕ Yeni Kullanıcı Ekle
        </button>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Yeni Kullanıcı Oluştur</h2>
            <form onSubmit={handleCreateUser} className="create-user-form">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={createUserData.email}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Şifre *</label>
                <input
                  type="password"
                  id="password"
                  value={createUserData.password}
                  onChange={(e) => setCreateUserData(prev => ({ ...prev, password: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">Ad *</label>
                  <input
                    type="text"
                    id="firstName"
                    value={createUserData.firstName}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, firstName: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Soyad *</label>
                  <input
                    type="text"
                    id="lastName"
                    value={createUserData.lastName}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, lastName: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="department">Departman</label>
                  <input
                    type="text"
                    id="department"
                    value={createUserData.department}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, department: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phoneNumber">Telefon</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={createUserData.phoneNumber}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Roller</label>
                <div className="roles-selection">
                  {availableRoles.map(role => (
                    <div key={role.roleId} className="role-checkbox">
                      <input
                        type="checkbox"
                        id={`role-${role.roleId}`}
                        checked={selectedRoles.includes(role.roleId)}
                        onChange={() => handleRoleToggle(role.roleId)}
                      />
                      <label htmlFor={`role-${role.roleId}`}>
                        {role.roleName}
                        {role.isCustom && <span className="custom-badge">Özel</span>}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => { setShowCreateForm(false); resetForm(); }}>
                  İptal
                </button>
                <button type="submit">Kullanıcı Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Roles Modal */}
      {editingUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingUser.firstName} {editingUser.lastName} - Rol Düzenle</h2>
            <div className="user-info">
              <p><strong>Email:</strong> {editingUser.email}</p>
              <p><strong>Mevcut Roller:</strong> {editingUser.roles.join(', ')}</p>
            </div>
            
            <div className="form-group">
              <label>Yeni Roller</label>
              <div className="roles-selection">
                {availableRoles.map(role => (
                  <div key={role.roleId} className="role-checkbox">
                    <input
                      type="checkbox"
                      id={`edit-role-${role.roleId}`}
                      checked={selectedRoles.includes(role.roleId)}
                      onChange={() => handleRoleToggle(role.roleId)}
                    />
                    <label htmlFor={`edit-role-${role.roleId}`}>
                      {role.roleName}
                      {role.isCustom && <span className="custom-badge">Özel</span>}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => { setEditingUser(null); setSelectedRoles([]); }}>
                İptal
              </button>
              <button onClick={handleUpdateUserRoles}>
                Rolleri Güncelle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Ad Soyad</th>
              <th>Email</th>
              <th>Departman</th>
              <th>Telefon</th>
              <th>Roller</th>
              <th>Oluşturma Tarihi</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.isActive ? 'inactive-user' : ''}>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>{user.department || '-'}</td>
                <td>{user.phoneNumber || '-'}</td>
                <td>
                  <div className="user-roles">
                    {user.roles.map(role => (
                      <span key={role} className="role-badge">{role}</span>
                    ))}
                  </div>
                </td>
                <td>{new Date(user.createdDate).toLocaleDateString('tr-TR')}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => startEditUserRoles(user)}
                      className="edit-button"
                      title="Rolleri Düzenle"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="delete-button"
                      title="Kullanıcıyı Sil"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="no-users">
            <p>Henüz kullanıcı bulunmuyor.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagementPage;